(function () {
  function initReviewVideoCarousel(section) {
    if (!section || section.dataset.reviewVideoCarouselInitialized === 'true') return;

    const track = section.querySelector('[data-review-video-carousel-track]');
    const prevButton = section.querySelector('[data-review-video-nav="prev"]');
    const nextButton = section.querySelector('[data-review-video-nav="next"]');
    const originalSlides = Array.from(track ? track.children : []).filter(function (slide) {
      return slide.hasAttribute('data-review-video-carousel-slide') && !slide.hasAttribute('data-clone');
    });

    if (!track || !originalSlides.length) return;

    section.dataset.reviewVideoCarouselInitialized = 'true';

    const soundState = new Map();
    const originalCount = originalSlides.length;
    const reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
    const autoSlideEnabled = section.dataset.autoSlideEnabled === 'true';
    const autoSlideInterval = Math.max(2000, Number(section.dataset.autoSlideInterval) || 4000);
    const autoSlideResumeDelay = Math.max(
      1000,
      Number(section.dataset.autoSlideResumeDelay) || 4000
    );
    const pauseAutoSlideOnHover = section.dataset.autoSlidePauseHover !== 'false';
    const videoAutoplayEnabled = section.dataset.videoAutoplay !== 'false';
    const transparentPixel = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';

    let slides = [];
    let activeSlide = null;
    let ticking = false;
    let isJumping = false;
    let resizeFrame = null;
    let autoSlideTimer = null;
    let resumeTimer = null;
    let isHovered = false;
    let isFocused = false;
    let isInteracting = false;
    let isSectionVisible = !('IntersectionObserver' in window);
    let visiblePosterHydrationQueued = false;

    function getVideoArea(slide) {
      return slide ? slide.querySelector('.review-video-carousel__video-area') : null;
    }

    function getVideoMount(slide) {
      return slide ? slide.querySelector('[data-review-video-mount]') : null;
    }

    function getVideoTemplate(slide) {
      return slide ? slide.querySelector('[data-review-video-template]') : null;
    }

    function getPosterImage(slide) {
      return slide ? slide.querySelector('[data-review-video-poster]') : null;
    }

    function getMountedVideo(slide) {
      const mount = getVideoMount(slide);
      return mount ? mount.querySelector('video') : null;
    }

    function loadPosterImage(image, priority) {
      if (!image || image.dataset.posterLoaded === 'true') return;

      const posterSrc = image.getAttribute('data-poster-src');

      if (!posterSrc) return;

      const posterSrcset = image.getAttribute('data-poster-srcset');
      const posterSizes = image.getAttribute('data-poster-sizes');
      const shouldPrioritize = priority === 'high';

      image.loading = shouldPrioritize ? 'eager' : 'lazy';
      image.setAttribute('fetchpriority', shouldPrioritize ? 'high' : 'low');

      if (posterSizes) {
        image.setAttribute('sizes', posterSizes);
      }

      if (posterSrcset) {
        image.setAttribute('srcset', posterSrcset);
      }

      image.src = posterSrc;
      image.dataset.posterLoaded = 'true';
    }

    function ensureSlidePoster(slide, priority) {
      loadPosterImage(getPosterImage(slide), priority);
    }

    function pauseVideo(video) {
      if (!video) return;

      video.pause();
      video.muted = true;
      video.defaultMuted = true;
      video.setAttribute('muted', 'muted');
    }

    function configureVideo(video, shouldUnmute) {
      if (!video) return;

      video.playsInline = true;
      video.loop = true;
      video.preload = 'none';
      video.defaultMuted = !shouldUnmute;
      video.muted = !shouldUnmute;
      video.setAttribute('playsinline', '');
      video.setAttribute('preload', 'none');

      if (shouldUnmute) {
        video.removeAttribute('muted');
        video.volume = 1;
      } else {
        video.setAttribute('muted', 'muted');
      }
    }

    function mountVideo(slide) {
      const mount = getVideoMount(slide);
      const template = getVideoTemplate(slide);
      const videoArea = getVideoArea(slide);

      if (!mount || !template || !videoArea) return null;

      let video = mount.querySelector('video');
      if (!video) {
        mount.innerHTML = '';
        mount.appendChild(template.content.cloneNode(true));
        mount.hidden = false;
        videoArea.classList.add('is-video-mounted');
        video = mount.querySelector('video');
      }

      return video;
    }

    function unmountVideo(slide) {
      const mount = getVideoMount(slide);
      const videoArea = getVideoArea(slide);
      const video = getMountedVideo(slide);

      if (video) {
        pauseVideo(video);
      }

      if (mount) {
        mount.innerHTML = '';
        mount.hidden = true;
      }

      if (videoArea) {
        videoArea.classList.remove('is-video-mounted');
      }
    }

    function isSlideVisible(slide) {
      if (!slide) return false;

      const trackRect = track.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      const overlap = Math.min(trackRect.right, slideRect.right) - Math.max(trackRect.left, slideRect.left);

      return overlap > Math.min(slideRect.width * 0.45, 120);
    }

    function shouldMountVideo(slide) {
      return !!(
        slide &&
        slide === activeSlide &&
        isSectionVisible &&
        !document.hidden &&
        isSlideVisible(slide)
      );
    }

    function hydrateVisiblePosters() {
      visiblePosterHydrationQueued = false;

      if (!isSectionVisible || document.hidden) return;

      slides.forEach(function (slide) {
        if (slide === activeSlide || !isSlideVisible(slide)) return;
        ensureSlidePoster(slide, 'low');
      });
    }

    function queueVisiblePosterHydration() {
      if (visiblePosterHydrationQueued || !isSectionVisible || document.hidden) return;

      visiblePosterHydrationQueued = true;

      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(hydrateVisiblePosters, { timeout: 1200 });
      } else {
        window.setTimeout(hydrateVisiblePosters, 280);
      }
    }

    function syncSlideMediaStates() {
      slides.forEach(function (slide) {
        const isActive = slide === activeSlide;
        slide.classList.toggle('is-active', isActive);

        if (isActive) {
          ensureSlidePoster(slide, 'high');
        }

        if (!getVideoTemplate(slide)) return;

        if (!shouldMountVideo(slide)) {
          unmountVideo(slide);
          return;
        }

        const originalIndex = Number(slide.dataset.reviewVideoIndex);
        const shouldUnmute = soundState.get(originalIndex) === true;
        const video = mountVideo(slide);

        if (!video) return;

        configureVideo(video, shouldUnmute);

        if (videoAutoplayEnabled || shouldUnmute) {
          const playPromise = video.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(function () {});
          }
        } else {
          video.pause();
        }
      });

      queueVisiblePosterHydration();
    }

    function getClosestSlide() {
      const trackRect = track.getBoundingClientRect();
      const trackCenter = trackRect.left + trackRect.width / 2;
      let closest = slides[0];
      let smallestDistance = Infinity;

      slides.forEach(function (slide) {
        const slideRect = slide.getBoundingClientRect();
        const slideCenter = slideRect.left + slideRect.width / 2;
        const distance = Math.abs(trackCenter - slideCenter);

        if (distance < smallestDistance) {
          smallestDistance = distance;
          closest = slide;
        }
      });

      return closest;
    }

    function getCanonicalSlide(originalIndex) {
      return (
        slides[originalCount + originalIndex] ||
        slides.find(function (slide) {
          return (
            slide.dataset.reviewVideoIndex === String(originalIndex) &&
            !slide.hasAttribute('data-clone')
          );
        }) ||
        null
      );
    }

    function setSlideActiveState(nextActiveSlide) {
      activeSlide = nextActiveSlide;
      syncSlideMediaStates();
    }

    function updateSoundButtons(originalIndex) {
      const shouldUnmute = soundState.get(originalIndex) === true;

      slides.forEach(function (slide) {
        if (slide.dataset.reviewVideoIndex !== String(originalIndex)) return;

        const soundButton = slide.querySelector('[data-review-video-sound]');
        if (!soundButton) return;

        soundButton.classList.toggle('is-unmuted', shouldUnmute);
        soundButton.classList.toggle('is-muted', !shouldUnmute);
        soundButton.setAttribute('aria-pressed', shouldUnmute ? 'true' : 'false');
        soundButton.setAttribute('aria-label', shouldUnmute ? 'Mute video' : 'Unmute video');
      });
    }

    function syncButtonStates() {
      for (let index = 0; index < originalCount; index += 1) {
        updateSoundButtons(index);
      }
    }

    function centerSlide(slide, behavior) {
      if (!slide) return;

      const left = slide.offsetLeft - track.clientWidth / 2 + slide.clientWidth / 2;
      track.scrollTo({
        left: left,
        behavior: behavior || 'smooth'
      });
    }

    function activateSlide(slide, behavior) {
      if (!slide) return;

      setSlideActiveState(slide);
      centerSlide(slide, behavior);
      requestActiveUpdate();
    }

    function normalizeLoopPosition() {
      if (!activeSlide || !activeSlide.hasAttribute('data-clone')) return;

      const originalIndex = Number(activeSlide.dataset.reviewVideoIndex);
      const canonicalSlide = getCanonicalSlide(originalIndex);

      if (!canonicalSlide) return;

      isJumping = true;
      centerSlide(canonicalSlide, 'auto');
      setSlideActiveState(canonicalSlide);

      window.requestAnimationFrame(function () {
        isJumping = false;
        setActiveSlide();
      });
    }

    function setActiveSlide() {
      if (isJumping) {
        ticking = false;
        return;
      }

      setSlideActiveState(getClosestSlide());

      if (activeSlide) {
        normalizeLoopPosition();
      }

      ticking = false;
    }

    function requestActiveUpdate() {
      if (ticking) return;

      ticking = true;
      window.requestAnimationFrame(setActiveSlide);
    }

    function clearAutoSlideTimer() {
      if (!autoSlideTimer) return;

      window.clearTimeout(autoSlideTimer);
      autoSlideTimer = null;
    }

    function clearResumeTimer() {
      if (!resumeTimer) return;

      window.clearTimeout(resumeTimer);
      resumeTimer = null;
    }

    function canAutoSlide() {
      return (
        autoSlideEnabled &&
        !reducedMotionMedia.matches &&
        !document.hidden &&
        isSectionVisible &&
        !isHovered &&
        !isFocused &&
        !isInteracting &&
        originalCount > 1
      );
    }

    function scheduleAutoSlide(delay) {
      clearAutoSlideTimer();

      if (!canAutoSlide()) return;

      autoSlideTimer = window.setTimeout(advanceAutoSlide, delay || autoSlideInterval);
    }

    function getNextSlide() {
      const currentSlide = activeSlide || getCanonicalSlide(0) || slides[originalCount] || slides[0];
      const currentIndex = slides.indexOf(currentSlide);

      if (currentIndex === -1) {
        return getCanonicalSlide(0) || slides[originalCount] || slides[0];
      }

      return slides[currentIndex + 1] || slides[originalCount] || slides[0];
    }

    function getPreviousSlide() {
      const currentSlide = activeSlide || getCanonicalSlide(0) || slides[originalCount] || slides[0];
      const currentIndex = slides.indexOf(currentSlide);

      if (currentIndex === -1) {
        return getCanonicalSlide(originalCount - 1) || slides[originalCount - 1] || slides[slides.length - 1];
      }

      return (
        slides[currentIndex - 1] ||
        slides[slides.length - 1] ||
        getCanonicalSlide(originalCount - 1) ||
        slides[0]
      );
    }

    function advanceAutoSlide() {
      if (!canAutoSlide()) return;

      const nextSlide = getNextSlide();
      if (!nextSlide) return;

      activateSlide(nextSlide, 'smooth');
      scheduleAutoSlide();
    }

    function pauseAutoSlideTemporarily(delay) {
      if (!autoSlideEnabled) return;

      isInteracting = true;
      clearAutoSlideTimer();
      clearResumeTimer();

      resumeTimer = window.setTimeout(function () {
        isInteracting = false;
        scheduleAutoSlide(260);
      }, delay || autoSlideResumeDelay);
    }

    function handleAutoSlideEnvironmentChange() {
      clearAutoSlideTimer();
      isHovered = pauseAutoSlideOnHover && typeof track.matches === 'function' ? track.matches(':hover') : false;
      isFocused = track.contains(document.activeElement);

      if (document.hidden || reducedMotionMedia.matches) {
        clearResumeTimer();
        isInteracting = false;
      }

      syncSlideMediaStates();
      scheduleAutoSlide(260);
    }

    function observeSectionVisibility() {
      const visibilityTarget = section.querySelector('.review-video-carousel__viewport') || section;

      if (!('IntersectionObserver' in window) || !visibilityTarget) return;

      const observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            isSectionVisible = entry.isIntersecting && entry.intersectionRatio > 0.12;
          });

          syncSlideMediaStates();
          scheduleAutoSlide(260);
        },
        {
          threshold: [0, 0.12, 0.25]
        }
      );

      observer.observe(visibilityTarget);
    }

    function removeExistingClones() {
      track.querySelectorAll('[data-clone="true"]').forEach(function (clone) {
        clone.remove();
      });
    }

    function cloneSlides() {
      removeExistingClones();

      const prependFragment = document.createDocumentFragment();
      const appendFragment = document.createDocumentFragment();

      function prepareClone(clone) {
        clone.setAttribute('data-clone', 'true');

        clone.querySelectorAll('[id]').forEach(function (element) {
          element.removeAttribute('id');
        });

        clone.querySelectorAll('[data-review-video-poster]').forEach(function (image) {
          image.removeAttribute('srcset');
          image.removeAttribute('sizes');
          image.setAttribute('src', transparentPixel);
          image.setAttribute('loading', 'lazy');
          image.setAttribute('fetchpriority', 'low');
          image.setAttribute('data-poster-loaded', 'false');
        });
      }

      originalSlides.forEach(function (slide) {
        const prependClone = slide.cloneNode(true);
        prepareClone(prependClone);
        prependFragment.appendChild(prependClone);

        const appendClone = slide.cloneNode(true);
        prepareClone(appendClone);
        appendFragment.appendChild(appendClone);
      });

      track.insertBefore(prependFragment, track.firstChild);
      track.appendChild(appendFragment);

      slides = Array.from(track.querySelectorAll('[data-review-video-carousel-slide]'));
    }

    function bindControls() {
      function isInteractiveTarget(target) {
        return !!target.closest(
          'a, button, input, select, textarea, label, summary, details, product-form, form, video'
        );
      }

      section.addEventListener('click', function (event) {
        const soundButton = event.target.closest('[data-review-video-sound]');
        if (soundButton) {
          event.preventDefault();
          event.stopPropagation();
          pauseAutoSlideTemporarily();

          const slide = soundButton.closest('[data-review-video-carousel-slide]');
          const originalIndex = Number(slide.dataset.reviewVideoIndex);
          const shouldUnmute = soundState.get(originalIndex) !== true;

          soundState.set(originalIndex, shouldUnmute);
          updateSoundButtons(originalIndex);

          if (slide === activeSlide) {
            setSlideActiveState(slide);
          } else {
            activateSlide(slide, 'smooth');
          }
          return;
        }

        if (isInteractiveTarget(event.target)) return;

        const slide = event.target.closest('[data-review-video-carousel-slide]');
        if (!slide || !track.contains(slide)) return;

        pauseAutoSlideTemporarily();
        activateSlide(slide, 'smooth');
      });
    }

    function bindDesktopNav() {
      const shouldDisable = originalCount < 2;

      [prevButton, nextButton].forEach(function (button) {
        if (!button) return;
        button.disabled = shouldDisable;
      });

      if (prevButton && prevButton.dataset.reviewVideoNavBound !== 'true') {
        prevButton.dataset.reviewVideoNavBound = 'true';
        prevButton.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation();
          pauseAutoSlideTemporarily();

          const previousSlide = getPreviousSlide();
          if (previousSlide) {
            activateSlide(previousSlide, 'smooth');
          }
        });
      }

      if (nextButton && nextButton.dataset.reviewVideoNavBound !== 'true') {
        nextButton.dataset.reviewVideoNavBound = 'true';
        nextButton.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation();
          pauseAutoSlideTemporarily();

          const nextSlide = getNextSlide();
          if (nextSlide) {
            activateSlide(nextSlide, 'smooth');
          }
        });
      }
    }

    cloneSlides();
    syncButtonStates();
    bindControls();
    bindDesktopNav();
    observeSectionVisibility();

    track.addEventListener('scroll', requestActiveUpdate, { passive: true });
    track.addEventListener('pointerdown', function () {
      pauseAutoSlideTemporarily();
    });
    track.addEventListener(
      'wheel',
      function () {
        pauseAutoSlideTemporarily();
      },
      { passive: true }
    );
    track.addEventListener(
      'touchstart',
      function () {
        pauseAutoSlideTemporarily();
      },
      { passive: true }
    );

    if (pauseAutoSlideOnHover) {
      track.addEventListener('mouseenter', function () {
        isHovered = true;
        clearAutoSlideTimer();
      });

      track.addEventListener('mouseleave', function () {
        isHovered = false;
        scheduleAutoSlide(260);
      });
    }

    track.addEventListener('focusin', function () {
      isFocused = true;
      clearAutoSlideTimer();
    });

    track.addEventListener('focusout', function () {
      window.setTimeout(function () {
        if (!track.contains(document.activeElement)) {
          isFocused = false;
          scheduleAutoSlide(260);
        }
      }, 0);
    });

    document.addEventListener('visibilitychange', handleAutoSlideEnvironmentChange);

    if (typeof reducedMotionMedia.addEventListener === 'function') {
      reducedMotionMedia.addEventListener('change', handleAutoSlideEnvironmentChange);
    } else if (typeof reducedMotionMedia.addListener === 'function') {
      reducedMotionMedia.addListener(handleAutoSlideEnvironmentChange);
    }

    window.addEventListener('resize', function () {
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(function () {
        const currentIndex = activeSlide ? Number(activeSlide.dataset.reviewVideoIndex) : 0;
        const canonicalSlide = getCanonicalSlide(currentIndex) || slides[originalCount] || slides[0];

        centerSlide(canonicalSlide, 'auto');
        setSlideActiveState(canonicalSlide);
        requestActiveUpdate();
        scheduleAutoSlide(260);
      });
    });

    centerSlide(getCanonicalSlide(0) || slides[originalCount] || slides[0], 'auto');
    window.setTimeout(function () {
      setActiveSlide();
    }, 80);
    scheduleAutoSlide();
  }

  function initAllReviewVideoCarousels() {
    const sections = document.querySelectorAll('[data-review-video-carousel]');

    if (!('IntersectionObserver' in window) || (window.Shopify && window.Shopify.designMode)) {
      sections.forEach(initReviewVideoCarousel);
      return;
    }

    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        initReviewVideoCarousel(entry.target);
      });
    }, { rootMargin: '700px 0px' });

    sections.forEach(function (section) {
      observer.observe(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllReviewVideoCarousels);
  } else {
    initAllReviewVideoCarousels();
  }

  document.addEventListener('shopify:section:load', function (event) {
    const section = event.target.querySelector('[data-review-video-carousel]');

    if (section) {
      section.dataset.reviewVideoCarouselInitialized = 'false';
      initReviewVideoCarousel(section);
    }
  });
})();
